import { groupBy } from 'containers/Shop/Helpers';
import { IDiscountStrategy, IDiscountStrategyAction, IGroupedDiscountStrategy } from 'models/shop/discountStrategies';
import { IStore } from 'redux/IStore';

export const REQUEST_START: string = 'discountstrategies/REQUEST_START';
export const REQUEST_FAILURE: string = 'discountstrategies/REQUEST_FAILURE';
export const REQUEST_SUCCESS: string = 'discountstrategies/REQUEST_SUCCESS';
export const RESET_DATA: string = 'discountstrategies/RESET_DATA';

const initialState: IGroupedDiscountStrategy = {
	groupedDiscountStrategies: undefined,
	isFetching: false,
	message: undefined,
	error: false,
	shopId: undefined,
};

export function discountStrategyReducer(state = initialState, action?: IDiscountStrategyAction) {
	switch (action.type) {
		case REQUEST_START:
			return {
				...state,
				isFetching: true,
				message: undefined,
				error: false,
				shopId: action.payload.shopId,
			};

		case REQUEST_SUCCESS:
			return {
				isFetching: false,
				message: undefined,
				error: false,
				...action.payload,
			};

		case REQUEST_FAILURE:
			return {
				...state,
				isFetching: false,
				message: action.payload.message,
				error: true,
			};

		case RESET_DATA:
		default:
			return state;
	}
}

export const getDiscountStrategies = (shopId) => (dispatch, getState: () => IStore) => {
	dispatch(discountStrategiesRequestStart(shopId));

	return fetch(
		getState().fetchDefault.baseAPI + `api/Shop/${shopId}/DiscountStrategies`,
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'GET',
		}),
	)
		.then((res) => {
			if (res.ok) {
				return res.json().then((res: IDiscountStrategy[]) => {
					// Filter out product discount strategies (created by adding a discount on manual booking item) from FE for now
					const filteredDiscountStrategies: IDiscountStrategy[] = res.filter((ds) => ds?.productId === null);
					dispatch(discountStrategiesRequestSuccess(shopId, groupBy(filteredDiscountStrategies, 'groupId')));
				});
			} else {
				return res.json().then((res) => dispatch(discountStrategiesRequestFail(res)));
			}
		})
		.catch((err) => {
			dispatch(discountStrategiesRequestFail(err));
		});
};

export const addDiscountStrategy = (getState: () => IStore, discountStrategy: IDiscountStrategy) =>
	fetch(
		getState().fetchDefault.baseAPI + 'api/Shop/DiscountStrategies',
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'POST',
			body: JSON.stringify({ ...discountStrategy, isActive: true }),
		}),
	);

export const editDiscountStrategy = (getState: () => IStore, discountStrategy: IDiscountStrategy) =>
	fetch(
		getState().fetchDefault.baseAPI + 'api/Shop/DiscountStrategies/' + discountStrategy.id,
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'PUT',
			body: JSON.stringify(discountStrategy),
		}),
	);

export const deleteDiscountStrategy = (getState: () => IStore, discountStrategy: IDiscountStrategy) =>
	fetch(
		getState().fetchDefault.baseAPI + 'api/Shop/DiscountStrategies/' + discountStrategy.id,
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'DELETE',
		}),
	);

export const deleteDiscountStrategyGroup = (dispatch, getState: () => IStore, shopId, groupId) =>
	fetch(
		getState().fetchDefault.baseAPI + `api/Shop/${shopId}/DiscountStrategiesGroup/` + groupId,
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'DELETE',
		}),
	).then(() => dispatch(getDiscountStrategies(shopId)));

/**
 * Add or edit discount group
 * @param dispatch
 * @param getState
 * @param shopId
 * @param discountStrategies
 * @param groupId if edit, provide groupId
 * @returns
 */
export const addEditBatchDiscountStrategies = (
	dispatch,
	getState: () => IStore,
	shopId,
	discountStrategies: IDiscountStrategy[],
	groupId,
) => {
	return fetch(
		getState().fetchDefault.baseAPI + `api/Shop/${shopId}/DiscountStrategies/${groupId || ''}`,
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'POST',
			body: JSON.stringify(discountStrategies),
		}),
	).then((res) => {
		const splitLocation = res.headers.get('location')?.split('/');

		let newGroupId = undefined;
		if (splitLocation && splitLocation?.length) {
			newGroupId = splitLocation[splitLocation.length - 1];
		}

		return dispatch(getDiscountStrategies(shopId)).then(() => newGroupId);
	});
};

export const discountStrategiesRequestStart = (shopId: string) => ({
	type: REQUEST_START,
	payload: { shopId },
});

export const discountStrategiesRequestSuccess = (
	shopId: string,
	groupedDiscountStrategies: IGroupedDiscountStrategy['groupedDiscountStrategies'],
): IDiscountStrategyAction => {
	return {
		type: REQUEST_SUCCESS,
		payload: { groupedDiscountStrategies, shopId },
	};
};

export const discountStrategiesRequestFail = (message): IDiscountStrategyAction => {
	return {
		type: REQUEST_FAILURE,
		payload: { message },
	};
};
